import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cookie from "react-cookies";
import toast from "./Toast";
import constants from "../../Constants";

const baseURL = constants.baseURL;
const POLL_MS = 15000;

/**
 * Order- or quote-scoped messaging thread (dispatch / support).
 * embedded=true: inline card on track-order; false: used inside floating drawer.
 */
const OrderTripChat = ({
  orderid = "",
  quoteid = "",
  embedded = false,
  listId = "trip-chat-messages",
  title = "Trip messages",
  subtitle = "Message our dispatch team about this booking. We typically reply within business hours.",
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasToken, setHasToken] = useState(() => !!cookie.load("quotetoken"));
  const listRef = useRef(null);
  const contextId = orderid || quoteid;

  useEffect(() => {
    const sync = () => setHasToken(!!cookie.load("quotetoken"));
    sync();
    const id = setInterval(sync, 2000);
    return () => clearInterval(id);
  }, [contextId]);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!contextId || !hasToken) return;
    setLoading(true);
    try {
      const response = await axios.get(
        baseURL +
          (quoteid
            ? "/customer-quote-comments?quoteid="
            : "/customer-order-comments?orderid=") +
          contextId,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: cookie.load("quotetoken"),
          },
        }
      );
      const data = response.data || [];
      setMessages(data);
      const prevCount = parseInt(
        localStorage.getItem("local-notifications") || "0",
        10,
      );
      if (data.length > prevCount) {
        localStorage.setItem("local-notifications", String(data.length));
      }
      scrollToBottom();
    } catch (error) {
      console.error(error);
      if (embedded) toast.error("Could not load messages");
    } finally {
      setLoading(false);
    }
  }, [contextId, quoteid, hasToken, scrollToBottom, embedded]);

  useEffect(() => {
    if (!contextId || !hasToken) return undefined;
    fetchMessages();
    const interval = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(interval);
  }, [contextId, hasToken, fetchMessages]);

  const handleSendMessage = async () => {
    const text = inputValue.trim();
    if (!text || !hasToken) return;
    setSending(true);
    try {
      await axios.post(
        baseURL +
          (quoteid
            ? "/customer-quote-comments"
            : "/customer-order-comments/send-message"),
        {
          message: text,
          quoteid: quoteid || undefined,
          orderid: orderid || undefined,
        },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: cookie.load("quotetoken"),
          },
        },
      );
      setInputValue("");
      await fetchMessages();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (!contextId) return null;

  const panelClass =
    "order-trip-chat" + (embedded ? " order-trip-chat--embedded" : "");

  return (
    <div className={panelClass} id={embedded ? "trip-messages" : undefined}>
      {embedded && (
        <div className="order-trip-chat-head">
          <div className="order-trip-chat-head-icon" aria-hidden="true">
            <i className="mdi mdi-message-text-outline" />
          </div>
          <div>
            <h3 className="order-trip-chat-title">{title}</h3>
            <p className="order-trip-chat-sub">{subtitle}</p>
          </div>
        </div>
      )}

      {!hasToken ? (
        <p className="order-trip-chat-muted">
          Open this order with your email on the track page to message dispatch.
        </p>
      ) : (
        <>
          <div
            ref={listRef}
            id={listId}
            className="order-trip-chat-messages"
            aria-live="polite"
          >
            {messages.length === 0 && !loading && (
              <p className="order-trip-chat-empty">
                No messages yet. Ask about pickup time, load details, or payment -
                dispatch will reply here.
              </p>
            )}
            {messages.map((item) => {
              const isCurrentUser =
                item.actorid && item.actorid.includes("@");
              const d = new Date(item.dateadded);
              const dateStr = d.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              });
              const timeStr = d.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });
              return (
                <div
                  key={
                    item.orderconversationid ||
                    item.quoteconversationid ||
                    item.dateadded
                  }
                  className={`chat-message ${isCurrentUser ? "user" : "other"}`}
                >
                  <div className="bubble-owner">
                    {item.adminfirstname ||
                      item.driverfirstname ||
                      item.customerfirstname ||
                      "Support"}
                    <div className="bubble-time">
                      {dateStr} {timeStr}
                    </div>
                  </div>
                  <div className="bubble">{item.message}</div>
                </div>
              );
            })}
            {loading && (
              <p className="order-trip-chat-loading">Loading messages…</p>
            )}
          </div>

          <div className="order-trip-chat-compose">
            <input
              type="text"
              placeholder="Message dispatch about this trip…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              disabled={sending || !hasToken}
              aria-label="Message dispatch"
            />
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={sending || !hasToken || !inputValue.trim()}
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderTripChat;
